<template>
  <div class="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-black via-gray-900/50 to-black relative overflow-hidden">
    <!-- Effet de particules dorées -->
    <div class="absolute inset-0 opacity-30">
      <div class="absolute top-20 left-10 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
      <div class="absolute top-40 right-20 w-1 h-1 bg-red-500 rounded-full animate-pulse"></div>
      <div class="absolute bottom-40 left-1/4 w-1.5 h-1.5 bg-yellow-300 rounded-full animate-bounce"></div>
      <div class="absolute bottom-20 right-1/3 w-1 h-1 bg-red-400 rounded-full animate-ping"></div>
    </div>
    <div class="max-w-7xl mx-auto">
      <!-- Image principale avec animation au chargement -->
      <div class="mb-12 flex justify-center overflow-hidden rounded-2xl shadow-2xl shadow-yellow-500/40 transform transition-all duration-700 hover:scale-[1.02] relative group">
        <div class="absolute inset-0 bg-gradient-to-t from-red-600/20 via-transparent to-yellow-600/20 rounded-2xl"></div>
        <img src="@/assets/Spearhead-battle.png" alt="Warhammer Age of Sigmar Battle" 
             class="w-full object-cover max-h-[550px] animate-fadeIn rounded-2xl relative z-10" />
        <div class="absolute inset-0 bg-gradient-to-r from-yellow-500/10 via-red-500/10 to-yellow-400/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      </div>
      
      <!-- Bannière de bienvenue avec animation -->
      <div class="mb-16 bg-gradient-to-br from-black/95 via-gray-900/40 to-black/95 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border-2 border-yellow-500/60 transform transition-all duration-700 hover:shadow-yellow-500/50 animate-slideUp relative group">
        <!-- Effet de brillance animé -->
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
        <div class="px-8 py-10 text-center relative">
          <div class="absolute inset-0 opacity-15 bg-gradient-to-br from-yellow-400 via-red-500 to-yellow-300 pattern-warhammer"></div>
          <div class="relative">
            <h1 class="text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-300 to-red-400 bg-clip-text text-transparent mb-6 animate-fadeIn flex items-center justify-center">
              <i class="fas fa-dragon mr-3 text-yellow-400 animate-pulse"></i> Bienvenue sur le site de la Ligue SPEARHEAD de L'Instant Ludique !
            </h1>
            <div class="max-w-3xl mx-auto p-6 bg-gradient-to-br from-gray-900/60 to-black/80 rounded-xl shadow-inner border border-yellow-500/40 backdrop-blur-sm">
              <p class="text-yellow-100/90 text-lg animate-fadeIn flex items-center" style="animation-delay: 200ms;">
                <i class="fas fa-dice-d20 mr-2 text-yellow-400"></i>
                Plongez dans l'univers épique de Warhammer Age of Sigmar avec notre ligue SPEARHEAD, une expérience de jeu rapide, stratégique et accessible à tous. Organisée par L'Instant Ludique, votre boutique dédiée aux jeux de société et de figurines, cette ligue est l'occasion idéale pour vous initier à SPEARHEAD ou perfectionner vos tactiques dans un cadre convivial et passionné.
              </p>
              <p class="text-yellow-100/90 text-lg mt-4 animate-fadeIn flex items-center" style="animation-delay: 400ms;">
                <i class="fas fa-trophy mr-2 text-yellow-400"></i>
                Ici, vous pourrez suivre l'évolution de la ligue, consulter les classements ainsi que les statistiques de chaque joueur ! Pour toute information, n'hésitez pas à nous contacter.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Utiliser le composant DashboardModules -->
      <DashboardModules 
        :recentMatches="formattedMatches" 
        :topPlayers="topPlayers" 
      />

      <!-- Boutons d'action -->
      <div class="flex flex-col md:flex-row gap-5 justify-center animate-slideUp" style="animation-delay: 300ms;">
        <button @click="goTo('/matches')" class="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-xl text-black bg-gradient-to-r from-yellow-500 via-yellow-400 to-red-500 hover:from-yellow-400 hover:via-yellow-300 hover:to-red-400 shadow-lg shadow-yellow-500/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-yellow-500/50 relative overflow-hidden group">
          <div class="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <i class="fas fa-plus-circle mr-2 relative z-10"></i>
          <span class="relative z-10">Enregistrer un match</span>
        </button>
        <button @click="goTo('/rankings')" class="inline-flex items-center justify-center px-8 py-4 border-2 border-yellow-500 text-base font-medium rounded-xl text-yellow-300 bg-black/80 hover:bg-gray-900/90 shadow-lg shadow-yellow-500/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-yellow-500/40 relative overflow-hidden group">
          <div class="absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <i class="fas fa-list-ol mr-2 relative z-10"></i>
          <span class="relative z-10">Voir les classements</span>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import DashboardModules from '@/components/DashboardModules.vue'
import { useRouter } from 'vue-router'

export default {
  name: 'HomeView',
  components: {
    DashboardModules
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    // Récupérer les données depuis le store
    const matches = computed(() => store.getters['match/allMatches'])
    const isLoading = computed(() => 
      store.getters['match/isLoading'] || 
      store.getters['classement/isLoading']
    )
    
    // Formater les données des matchs pour le composant DashboardModules
    const formattedMatches = computed(() => {
      // Prendre les 5 derniers matchs
      return matches.value
        .slice(0, 5)
        .map(match => {
          // Formater la date en français
          let date = match.date;
          if (date && typeof date === 'object' && date.year && date.monthValue && date.dayOfMonth) {
            // Si c'est un objet LocalDate (cas backend Java)
            date = new Date(date.year, date.monthValue - 1, date.dayOfMonth);
          } else {
            date = new Date(date);
          }
          const formattedDate = date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
          return {
            player1: match.user1?.username || match.user1?.name || 'Joueur 1',
            player2: match.user2?.username || match.user2?.name || 'Joueur 2',
            score1: match.scoreUser1 ?? 0,
            score2: match.scoreUser2 ?? 0,
            date: formattedDate,
            armee1: match.armee1,
            armee2: match.armee2
          };
        });
    })
    
    // Récupérer les données de classement pour les meilleurs joueurs
    const topPlayers = computed(() => {
      const players = store.getters['classement/topPlayers'] || [];
      return players.slice(0, 5).map(player => ({
        name: player.username || player.name || 'Joueur',
        points: player.elo ?? 0,
        gamesPlayed: player.gamesPlayed ?? 0,
        winRate: player.winRate ?? '0%',
        rank: player.rank ?? null
      }));
    })
    
    // Charger les données au montage du composant
    onMounted(async () => {
      try {
        // Charger les données depuis l'API
        await Promise.all([
          store.dispatch('match/fetchAllMatches'),
          store.dispatch('classement/fetchTopPlayers')
        ])
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
      }
    })

    // Fonction pour scroller en haut après navigation
    const goTo = (route) => {
      router.push(route).then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    }

    return {
      formattedMatches,
      topPlayers,
      isLoading,
      goTo
    }
  }
}
</script>

<style scoped>
.prose {
  color: rgba(245, 208, 120, 0.8);
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes slideUp {
  0% { 
    opacity: 0;
    transform: translateY(30px);
  }
  100% { 
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.8s ease-out forwards;
}

.animate-slideUp {
  animation: slideUp 0.8s ease-out forwards;
}

.pattern-warhammer {
  background-image: 
    url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 0L0 12l12 12 12-12z' fill='%23F59E0B' fill-opacity='0.4'/%3E%3C/svg%3E"),
    url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0l20 20-20 20L0 20z' fill='%23EF4444' fill-opacity='0.3'/%3E%3C/svg%3E"),
    url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 0L0 8l8 8 8-8z' fill='%23FCD34D' fill-opacity='0.2'/%3E%3C/svg%3E");
  background-size: 30px 30px, 60px 60px, 20px 20px;
  animation: patternFloat 20s ease-in-out infinite;
}

@keyframes patternFloat {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(1deg); }
}

.box-glow {
  box-shadow: 0 0 40px rgba(245, 158, 11, 0.3), 
              0 0 20px rgba(239, 68, 68, 0.2),
              0 5px 20px rgba(0, 0, 0, 0.6), 
              inset 0 0 20px rgba(245, 158, 11, 0.1);
  transition: box-shadow 0.3s ease-in-out;
}

.box-glow:hover {
  box-shadow: 0 0 50px rgba(245, 158, 11, 0.4), 
              0 0 30px rgba(239, 68, 68, 0.3),
              0 0 15px rgba(252, 211, 77, 0.3),
              0 5px 20px rgba(0, 0, 0, 0.7), 
              inset 0 0 25px rgba(245, 158, 11, 0.2);
}

.hover\:scale-101:hover {
  transform: scale(1.01);
}

.shadow-glow {
  box-shadow: 0 0 10px rgba(217, 119, 6, 0.3);
}

.animate-pulse-slow {
  animation: pulse 3s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

/* Nouvelles animations pour l'effet Age of Sigmar */
@keyframes magicGlow {
  0%, 100% { 
    box-shadow: 0 0 20px rgba(245, 158, 11, 0.4), 
                0 0 40px rgba(239, 68, 68, 0.3);
  }
  50% { 
    box-shadow: 0 0 30px rgba(245, 158, 11, 0.6), 
                0 0 60px rgba(239, 68, 68, 0.4),
                0 0 20px rgba(252, 211, 77, 0.5);
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.magic-glow {
  animation: magicGlow 3s ease-in-out infinite;
}

.shimmer-effect {
  position: relative;
  overflow: hidden;
}

.shimmer-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  animation: shimmer 2s infinite;
}
</style> 